import Link from 'next/link';
import type { JSX } from 'react';
import type { CurrentUser } from '../portal';
import { sbText } from '../portal/i18nCatalogue';

/**
 * Narrow vertical icon column rendered to the left of page content
 * for shells that opt in. Configured via the host's
 * {@code shell.leftRail} block of the shell config.
 *
 * <h3>Behaviour</h3>
 * <ul>
 *   <li>Renders only when {@code config.enabled} is true.</li>
 *   <li>Hidden on viewports below 768px when {@code collapsedOnMobile}
 *       is true (default). On mobile the items belong in the
 *       hamburger menu rather than stealing horizontal space.</li>
 *   <li>Each item filters by auth state via {@code requireAuth} (same
 *       enum the menu uses). Items without {@code requireAuth} render
 *       to everyone.</li>
 *   <li>Icons come from a tiny built-in SVG map keyed on the
 *       {@code icon} string — we don't pull lucide-react onto the
 *       critical render path just to render four icons.</li>
 * </ul>
 *
 * <h3>URL construction</h3>
 *
 * Hosts pass {@code tenantHref} to resolve item hrefs into final URLs
 * (e.g. prepending a tenant slug). The runtime stays tenant-agnostic.
 */
export interface LeftRailItem {
  /** Icon key — see the built-in {@code ICONS} map. */
  icon: string;
  /** Tooltip / accessible label. */
  label: string;
  /** URL or host-relative href passed back through {@code tenantHref}. */
  href: string;
  /** Auth gate. */
  requireAuth?: 'always' | 'public' | 'authenticated' | 'permission';
  /** Required permissions when {@code requireAuth === 'permission'}. ANY match passes. */
  requirePerms?: string[];
  /** User must belong to AT LEAST ONE of these dynamic groups. Empty /
   *  absent = no audience gate. Applied independently of, and in
   *  addition to, {@code requireAuth} — an item can combine e.g.
   *  {@code requireAuth: 'authenticated'} with a group restriction. */
  requireDynamicGroupIds?: number[];
  /** User must belong to AT LEAST ONE of these security groups. Same
   *  independent-axis semantics as {@code requireDynamicGroupIds}. */
  requireSecurityGroupIds?: number[];
}

export interface LeftRailConfig {
  enabled?: boolean;
  items?: LeftRailItem[];
  collapsedOnMobile?: boolean;
}

export interface LeftRailProps {
  config: LeftRailConfig | undefined;
  currentUser: CurrentUser | null;
  /** Host-supplied href resolver. */
  tenantHref: (href: string) => string;
}

export function LeftRail({ config, currentUser, tenantHref }: LeftRailProps) {
  if (!config?.enabled) return null;

  const items = (config.items ?? []).filter((it) => visibleFor(it, currentUser));
  if (items.length === 0) return null;

  const hideOnMobile = config.collapsedOnMobile !== false;

  return (
    <aside
      aria-label={sbText('leftRail.label')}
      className={hideOnMobile ? 'sb-left-rail-mobile-hidden' : undefined}
      style={{
        width: '64px',
        flexShrink: 0,
        position: 'sticky',
        top: '72px',
        alignSelf: 'flex-start',
        zIndex: 40,
        paddingTop: '20px',
        paddingBottom: '24px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '12px',
        background: 'transparent',
      }}
    >
      {items.map((it, i) => (
        <span
          key={i}
          className="sb-rail-item"
          style={{ position: 'relative', display: 'inline-flex' }}
        >
          <Link
            href={tenantHref(it.href)}
            aria-label={it.label}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '40px',
              height: '40px',
              borderRadius: 'var(--radius-md, 8px)',
              border: '1px solid var(--color-border-default, rgba(255,255,255,0.18))',
              background: 'transparent',
              color: 'var(--color-text-secondary, rgba(255,255,255,0.78))',
              textDecoration: 'none',
              transition: 'background 120ms ease, color 120ms ease, border-color 120ms ease',
            }}
          >
            <RailIcon name={it.icon} size={18} />
          </Link>
          <span className="sb-rail-tooltip" role="tooltip" aria-hidden>
            {it.label}
          </span>
        </span>
      ))}

      <style dangerouslySetInnerHTML={{ __html: RAIL_STYLES }} />
    </aside>
  );
}

const RAIL_STYLES = `
  @media (max-width: 767px) {
    .sb-left-rail-mobile-hidden { display: none !important; }
  }
  .sb-rail-tooltip {
    position: absolute;
    left: calc(100% + 12px);
    top: 50%;
    transform: translateY(-50%) translateX(-4px);
    white-space: nowrap;
    padding: 6px 12px;
    border-radius: 6px;
    background: var(--color-surface-card, #1a1a1a);
    color: var(--color-text-primary, #ffffff);
    border: 1px solid var(--color-border-default, rgba(255,255,255,0.12));
    font-size: 12px;
    font-weight: 500;
    line-height: 1;
    box-shadow: 0 6px 24px rgba(0,0,0,0.35);
    opacity: 0;
    pointer-events: none;
    transition: opacity 120ms ease, transform 160ms ease;
    z-index: 100;
  }
  .sb-rail-tooltip::before {
    content: "";
    position: absolute;
    left: -4px;
    top: 50%;
    width: 8px;
    height: 8px;
    transform: translateY(-50%) rotate(45deg);
    background: var(--color-surface-card, #1a1a1a);
    border-left: 1px solid var(--color-border-default, rgba(255,255,255,0.12));
    border-bottom: 1px solid var(--color-border-default, rgba(255,255,255,0.12));
  }
  .sb-rail-item:hover .sb-rail-tooltip,
  .sb-rail-item:focus-within .sb-rail-tooltip {
    opacity: 1;
    transform: translateY(-50%) translateX(0);
  }
`;

function visibleFor(it: LeftRailItem, user: CurrentUser | null): boolean {
  const rule = it.requireAuth ?? 'always';
  if (rule === 'always') {
    // fall through to the group check below
  } else if (rule === 'public') {
    if (user != null) return false;
  } else if (rule === 'authenticated') {
    if (user == null) return false;
  } else if (rule === 'permission') {
    if (!user) return false;
    if (!user.permissions?.includes('*')) {
      const required = it.requirePerms ?? [];
      if (!required.some((p) => user.permissions?.includes(p))) return false;
    }
  }

  const groupIds = it.requireDynamicGroupIds;
  if (groupIds && groupIds.length > 0) {
    if (!user) return false;
    const userGroupIds = user.dynamicGroupIds ?? [];
    if (!groupIds.some((g) => userGroupIds.includes(g))) return false;
  }

  const securityGroupIds = it.requireSecurityGroupIds;
  if (securityGroupIds && securityGroupIds.length > 0) {
    if (!user) return false;
    const userSecurityGroupIds = user.securityGroupIds ?? [];
    if (!securityGroupIds.some((g) => userSecurityGroupIds.includes(g))) return false;
  }

  return true;
}

function RailIcon({ name, size = 18 }: { name: string; size?: number }) {
  const Icon = ICONS[name] ?? ICONS['fallback']!;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <Icon />
    </svg>
  );
}

const ICONS: Record<string, () => JSX.Element> = {
  play: () => (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2.5" />
      <path d="M10 9.5l4.5 2.5L10 14.5z" fill="currentColor" stroke="none" />
    </>
  ),
  star: () => (
    <path d="M12 3l2.6 5.3 5.9.85-4.27 4.16 1 5.84L12 16.4l-5.27 2.75 1-5.84L3.5 9.15l5.9-.85L12 3z" />
  ),
  bookmark: () => <path d="M6 3h12v18l-6-4-6 4z" />,
  heart: () => (
    <path d="M12 21s-7.5-4.5-9.4-9.5C1 7.5 4 4 7.5 4c1.7 0 3.4 1 4.5 2.5C13.1 5 14.8 4 16.5 4 20 4 23 7.5 21.4 11.5 19.5 16.5 12 21 12 21z" />
  ),
  bell: () => (
    <>
      <path d="M6 9a6 6 0 1 1 12 0c0 4 1.5 5.5 2 6.5H4c.5-1 2-2.5 2-6.5z" />
      <path d="M9.5 18a2.5 2.5 0 0 0 5 0" />
    </>
  ),
  compass: () => (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M16 8l-2 6-6 2 2-6 6-2z" />
    </>
  ),
  trophy: () => (
    <>
      <path d="M8 4h8v5a4 4 0 0 1-8 0V4z" />
      <path d="M5 5h3v2a3 3 0 0 1-3-3z" />
      <path d="M19 5h-3v2a3 3 0 0 0 3-3z" />
      <path d="M9 14h6v3l1 3H8l1-3v-3z" />
    </>
  ),
  fallback: () => <circle cx="12" cy="12" r="3" />,
};
